import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  DollarSign,
  TrendingUp,
  Users,
  Crown,
  UserPlus,
  Sparkles,
  Loader2,
  AlertTriangle,
  ShoppingBag,
  ExternalLink,
} from "lucide-react";
import AdminShell from "../components/AdminShell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtUSD = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatTile = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div
    className="bg-white/[0.04] border border-white/10 rounded-2xl p-6"
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-[0.22em] text-white/55">
        {label}
      </span>
      <div
        className={`h-9 w-9 rounded-lg flex items-center justify-center border ${
          accent === "gold"
            ? "bg-[#D4B483]/15 border-[#D4B483]/25 text-[#D4B483]"
            : accent === "rose"
              ? "bg-[#D5A8A4]/15 border-[#D5A8A4]/25 text-[#D5A8A4]"
              : "bg-white/5 border-white/10 text-white/70"
        }`}
      >
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <div className="text-3xl font-light text-white tabular-nums">{value}</div>
    {sub && <div className="text-xs text-white/45 mt-2">{sub}</div>}
  </div>
);

const StatusPill = ({ status }) => {
  const styles =
    status === "paid"
      ? "bg-emerald-400/10 text-emerald-300 border-emerald-400/25"
      : status === "open" || status === "initiated"
        ? "bg-amber-400/10 text-amber-300 border-amber-400/25"
        : status === "expired" || status === "canceled"
          ? "bg-white/5 text-white/45 border-white/10"
          : "bg-white/5 text-white/55 border-white/10";
  return (
    <span
      className={`inline-flex items-center text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${styles}`}
    >
      {status || "unknown"}
    </span>
  );
};

const ProviderRow = ({ provider, count, new_last_30d, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  const human =
    {
      google: "Google",
      magic_link: "Magic link",
      password: "Email + password",
      stripe_checkout: "Stripe Checkout",
      guest: "Guest / Stealth",
      test: "Test / seed",
      unknown: "Unknown",
    }[provider] || provider;
  return (
    <div
      className="flex items-center gap-4 py-3"
      data-testid={`signup-row-${provider}`}
    >
      <div className="w-36 text-sm text-white/85">{human}</div>
      <div className="flex-1">
        <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#D4B483] to-[#D5A8A4]"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
      <div className="w-20 text-right text-sm text-white/85 tabular-nums">
        {count.toLocaleString()}
      </div>
      <div className="w-16 text-right text-xs text-white/45 tabular-nums">
        {pct}%
      </div>
      <div className="w-28 text-right text-xs text-emerald-300/80 tabular-nums">
        {new_last_30d > 0 ? `+${new_last_30d} 30d` : "—"}
      </div>
    </div>
  );
};

const SimpleBar = ({ items, totalKey = "count" }) => {
  if (!items?.length) {
    return <p className="text-sm text-white/40 italic">No data yet.</p>;
  }
  const total = items.reduce((s, i) => s + (i[totalKey] || 0), 0) || 1;
  return (
    <div className="space-y-2">
      {items.map((item) => {
        const label = item.value ?? item.category ?? "—";
        const n = item[totalKey] || 0;
        const pct = Math.round((n / total) * 100);
        return (
          <div key={String(label)} className="flex items-center gap-3">
            <div className="w-28 text-sm text-white/75 truncate capitalize">
              {String(label)}
            </div>
            <div className="flex-1 h-2 rounded-full bg-white/[0.06] overflow-hidden">
              <div
                className="h-full bg-[#D4B483]/70"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="w-12 text-right text-xs text-white/55 tabular-nums">
              {n}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AdminRevenue = () => {
  const [revenue, setRevenue] = useState(null);
  const [signups, setSignups] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rev, sig] = await Promise.all([
          axios.get(`${API}/admin/revenue`, { withCredentials: true }),
          axios.get(`${API}/admin/signups`, { withCredentials: true }),
        ]);
        if (!cancelled) {
          setRevenue(rev.data);
          setSignups(sig.data);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.status === 403
              ? "You're signed in, but this view is admin-only."
              : "We couldn't load revenue data right now.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <AdminShell title="Revenue & sign-ups">
        <div
          className="flex items-center gap-3 text-white/70"
          data-testid="admin-revenue-loading"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading the books…
        </div>
      </AdminShell>
    );
  }

  if (error) {
    return (
      <AdminShell title="Revenue & sign-ups">
        <div
          className="bg-red-500/10 border border-red-400/30 rounded-2xl p-6 text-red-200"
          data-testid="admin-revenue-error"
        >
          <AlertTriangle className="h-5 w-5 mb-2" />
          {error}
        </div>
      </AdminShell>
    );
  }

  const totalSignups = signups?.total_signups || 0;
  const guestTotal = signups?.guest_sessions?.total || 0;
  const guestNew30d = signups?.guest_sessions?.new_last_30d || 0;

  return (
    <AdminShell title="Revenue & sign-ups">
      <div className="space-y-10" data-testid="admin-revenue-root">
        {/* ── Money tiles ──────────────────────────────────────── */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.25em] text-white/45 mb-4">
            Money
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile
              icon={DollarSign}
              label="Lifetime revenue"
              value={fmtUSD(revenue?.lifetime?.revenue)}
              sub={`${revenue?.lifetime?.paid_count || 0} paid order${revenue?.lifetime?.paid_count === 1 ? "" : "s"}`}
              accent="gold"
              testid="rev-tile-lifetime"
            />
            <StatTile
              icon={TrendingUp}
              label="Last 30 days"
              value={fmtUSD(revenue?.last_30d?.revenue)}
              sub={`${revenue?.last_30d?.paid_count || 0} paid in window`}
              accent="rose"
              testid="rev-tile-30d"
            />
            <StatTile
              icon={Crown}
              label="Active Pro"
              value={(revenue?.active_pro_subscribers || 0).toLocaleString()}
              sub={(() => {
                const s = revenue?.active_pro_by_source || {};
                const parts = [
                  s.paid ? `${s.paid} paid` : null,
                  s.gift_code ? `${s.gift_code} gift` : null,
                  s.manual ? `${s.manual} manual` : null,
                  s.admin ? `${s.admin} admin` : null,
                  s.unknown ? `${s.unknown} review` : null,
                ].filter(Boolean);
                return parts.length
                  ? parts.join(" · ")
                  : "Within current pass window";
              })()}
              accent="gold"
              testid="rev-tile-active-pro"
            />
            <StatTile
              icon={Sparkles}
              label="MRR estimate"
              value={fmtUSD(revenue?.mrr_estimate)}
              sub={`Paid Pros × ${fmtUSD(revenue?.pro_price)} (gifts & admin excluded)`}
              testid="rev-tile-mrr"
            />
          </div>
        </section>

        {/* ── Sign-ups breakdown ───────────────────────────────── */}
        <section>
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="text-xs uppercase tracking-[0.25em] text-white/45">
                Sign-ups
              </h2>
              <p className="text-[11px] text-white/40 mt-1">
                People who actually created an account (Google · Magic link · Password · Stripe Checkout).
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-white/55">
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                {totalSignups.toLocaleString()} total
              </span>
              <span className="inline-flex items-center gap-1.5 text-emerald-300/80">
                <UserPlus className="h-3.5 w-3.5" />
                {(signups?.new_last_30d || 0).toLocaleString()} new (30d)
              </span>
            </div>
          </div>
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl px-5 py-3 divide-y divide-white/5">
            {(signups?.by_provider || []).map((row) => (
              <ProviderRow
                key={row.provider}
                {...row}
                total={totalSignups}
              />
            ))}
            {!signups?.by_provider?.length && (
              <p className="py-6 text-sm text-white/40 italic">
                No sign-ups yet.
              </p>
            )}
          </div>
          <div
            className="mt-3 px-5 py-3 bg-white/[0.02] border border-white/5 rounded-xl flex items-center justify-between text-xs text-white/55"
            data-testid="signup-guest-sessions"
          >
            <span>
              + <span className="text-white/75 tabular-nums">{guestTotal.toLocaleString()}</span>{" "}
              guest / Stealth Mode sessions (no account created)
            </span>
            <span className="text-emerald-300/70 tabular-nums">
              {guestNew30d > 0 ? `+${guestNew30d} in 30d` : "—"}
            </span>
          </div>
        </section>

        {/* ── What they signed up for ──────────────────────────── */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.25em] text-white/45 mb-4">
            What they came for
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div
              className="bg-white/[0.04] border border-white/10 rounded-2xl p-5"
              data-testid="signup-goals"
            >
              <h3 className="text-sm text-white/85 mb-3">Goal</h3>
              <SimpleBar items={signups?.onboarding?.goal} />
            </div>
            <div
              className="bg-white/[0.04] border border-white/10 rounded-2xl p-5"
              data-testid="signup-experience"
            >
              <h3 className="text-sm text-white/85 mb-3">Experience</h3>
              <SimpleBar items={signups?.onboarding?.experience} />
            </div>
            <div
              className="bg-white/[0.04] border border-white/10 rounded-2xl p-5"
              data-testid="signup-most-skipped"
            >
              <h3 className="text-sm text-white/85 mb-3">
                Most-skipped categories
              </h3>
              <SimpleBar
                items={signups?.onboarding?.most_skipped_categories}
              />
            </div>
          </div>
          <p className="mt-4 text-xs text-white/40">
            Pulled from the {signups?.onboarding?.total_routines_created || 0}{" "}
            routines generated so far.
          </p>
        </section>

        {/* ── Transactions table ───────────────────────────────── */}
        <section data-testid="rev-section-transactions">
          <div className="flex items-end justify-between mb-4">
            <h2 className="text-xs uppercase tracking-[0.25em] text-white/45">
              Transactions
            </h2>
            <div className="text-xs text-white/45">
              Latest {revenue?.transactions?.length || 0} · user_id only (no
              emails)
            </div>
          </div>
          <div className="bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-white/45 border-b border-white/10">
              <div className="col-span-3">When</div>
              <div className="col-span-4">User</div>
              <div className="col-span-2">Package</div>
              <div className="col-span-1 text-right">Amount</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            <div className="divide-y divide-white/5">
              {(revenue?.transactions || []).map((t) => (
                <div
                  key={t.session_id}
                  className="grid grid-cols-12 px-5 py-3 items-center text-sm"
                  data-testid={`txn-row-${t.session_id}`}
                >
                  <div className="col-span-3 text-white/70 text-xs">
                    {fmtDate(t.created_at)}
                  </div>
                  <div className="col-span-4 font-mono text-xs text-white/60 truncate">
                    <Link
                      to={`/admin/customers/${t.user_id}`}
                      className="hover:text-[#D4B483] hover:underline transition-colors"
                      data-testid={`customer-link-${t.user_id}`}
                      title="Open customer detail (audit-logged)"
                    >
                      {t.user_id}
                    </Link>
                  </div>
                  <div className="col-span-2 text-white/75">
                    {t.package_id}
                  </div>
                  <div className="col-span-1 text-right text-white/85 tabular-nums">
                    {fmtUSD(t.amount)}
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <StatusPill status={t.payment_status} />
                  </div>
                </div>
              ))}
              {!revenue?.transactions?.length && (
                <p className="px-5 py-8 text-sm text-white/40 italic">
                  No transactions yet — your first paid order will land here.
                </p>
              )}
            </div>
          </div>

          {revenue?.status_breakdown?.length > 0 && (
            <div className="mt-3 text-xs text-white/45">
              Status totals:{" "}
              {revenue.status_breakdown.map((s, i) => (
                <span key={s.status}>
                  {i > 0 && " · "}
                  <span className="text-white/65">{s.status}</span> ({s.count}
                  {s.revenue > 0 ? ` · ${fmtUSD(s.revenue)}` : ""})
                </span>
              ))}
            </div>
          )}
          {revenue?.stale_checkouts_count > 0 && (
            <div
              className="mt-2 text-xs text-white/45"
              data-testid="rev-stale-checkouts-note"
            >
              + <span className="text-white/65">
                {revenue.stale_checkouts_count}
              </span>{" "}
              expired/abandoned checkouts (auto-hidden — Stripe expires
              sessions after 24h)
            </div>
          )}
        </section>

        {/* ── Affiliate clicks (Amazon Associates) ────────────── */}
        <section data-testid="rev-section-affiliate">
          <div className="flex items-end justify-between mb-4">
            <div>
              <h2 className="text-xs uppercase tracking-[0.25em] text-white/45">
                Affiliate clicks
              </h2>
              <p className="text-[11px] text-white/40 mt-1">
                Outbound product clicks. Amazon commissions appear in your
                Associates dashboard 24–48h after a sale settles —
                reconcile monthly.{" "}
                {revenue?.affiliate?.amazon_tag && (
                  <span className="text-white/55">
                    Tag: {revenue.affiliate.amazon_tag}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <StatTile
              icon={ShoppingBag}
              label="Lifetime clicks"
              value={(
                revenue?.affiliate?.lifetime_clicks || 0
              ).toLocaleString()}
              sub="All product redirects"
              testid="aff-tile-lifetime"
            />
            <StatTile
              icon={ShoppingBag}
              label="Last 30 days"
              value={(
                revenue?.affiliate?.last_30d_clicks || 0
              ).toLocaleString()}
              sub="All programs"
              testid="aff-tile-30d"
              accent="rose"
            />
            <StatTile
              icon={ExternalLink}
              label="Amazon clicks · 30d"
              value={(
                revenue?.affiliate?.amazon_clicks_30d || 0
              ).toLocaleString()}
              sub="Eligible for commission"
              accent="gold"
              testid="aff-tile-amazon-30d"
            />
            <StatTile
              icon={ExternalLink}
              label="Amazon · lifetime"
              value={(
                revenue?.affiliate?.amazon_clicks_lifetime || 0
              ).toLocaleString()}
              sub="Tagged + tracked"
              testid="aff-tile-amazon-life"
            />
          </div>

          <div
            className="bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden"
            data-testid="aff-top-products"
          >
            <div className="grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-white/45 border-b border-white/10">
              <div className="col-span-5">Product</div>
              <div className="col-span-3">Brand</div>
              <div className="col-span-2">Program</div>
              <div className="col-span-2 text-right">Clicks</div>
            </div>
            <div className="divide-y divide-white/5">
              {(revenue?.affiliate?.top_products || []).map((p) => (
                <div
                  key={p.product_id}
                  className="grid grid-cols-12 px-5 py-3 items-center text-sm"
                  data-testid={`aff-top-row-${p.product_id}`}
                >
                  <div className="col-span-5 text-white/85 truncate">
                    {p.name}
                  </div>
                  <div className="col-span-3 text-white/65 truncate">
                    {p.brand}
                  </div>
                  <div className="col-span-2">
                    <span
                      className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border ${
                        p.program === "amazon"
                          ? "bg-[#D4B483]/10 text-[#D4B483] border-[#D4B483]/30"
                          : p.program === "sephora"
                            ? "bg-[#D5A8A4]/10 text-[#D5A8A4] border-[#D5A8A4]/30"
                            : "bg-white/5 text-white/55 border-white/10"
                      }`}
                    >
                      {p.program}
                    </span>
                  </div>
                  <div className="col-span-2 text-right text-white/85 tabular-nums">
                    {p.clicks.toLocaleString()}
                  </div>
                </div>
              ))}
              {!revenue?.affiliate?.top_products?.length && (
                <p className="px-5 py-8 text-sm text-white/40 italic">
                  No clicks tracked yet — first product click lands here.
                </p>
              )}
            </div>
          </div>
        </section>
      </div>
    </AdminShell>
  );
};

export default AdminRevenue;
